import React from "react";
import { useTranslation } from "react-i18next";

import type { TreeOutlineEntry } from "./treeGraph.types";

type TreeOutlineProps = {
	entries: TreeOutlineEntry[];
};

/**
 * Text alternative for the graph canvas.
 *
 * The canvas conveys the tree through position and connecting lines, neither of which reaches a
 * screen reader — the nodes announce their own names but nothing describes how they relate. This
 * renders the same information as a nested list: generations in order, the entities on each one in
 * the order they appear left to right, and each entity's relations phrased the way the canvas
 * already phrases them in its edge labels.
 *
 * Visually hidden rather than absent, so it stays in the accessibility tree and in the document
 * order right before the canvas it describes.
 */
const TreeOutline = ({ entries }: TreeOutlineProps) => {
	const { t } = useTranslation("homePage");

	if (entries.length === 0) {
		return null;
	}

	const generations = Array.from(new Set(entries.map((entry: TreeOutlineEntry) => entry.generation))).sort(
		(a: number, b: number) => a - b
	);

	return (
		<div className="sr-only">
			<h2>{t("view.outlineHeading")}</h2>
			<p>{t("view.outlineSummary", { count: entries.length, generations: generations.length })}</p>
			<ol>
				{generations.map((generation: number, index: number) => (
					<li key={generation}>
						{t("view.outlineGeneration", { number: index + 1, total: generations.length })}
						<ul>
							{entries
								.filter((entry: TreeOutlineEntry) => entry.generation === generation)
								.map((entry: TreeOutlineEntry) => (
									<li key={entry.id}>
										{t("view.outlineEntity", { title: entry.title, type: entry.type })}
										{entry.relations.length > 0 && (
											<ul>
												{entry.relations.map((relation: string) => (
													<li key={relation}>{relation}</li>
												))}
											</ul>
										)}
									</li>
								))}
						</ul>
					</li>
				))}
			</ol>
		</div>
	);
};

export default TreeOutline;
